import type { QueryClient } from '@tanstack/react-query';
import {
  BaseModel,
  QueryClientEventWatchable,
} from '@cotera/client/v0/actions/framework';
import { WatchablesFactory } from '../watchables/watchables-factory';

/**
 * Keeps org-scoped TanStack Query caches fresh on websocket broadcasts, using the
 * same QueryClientEventWatchable pattern as Coco list-page models (bus subscription +
 * invalidate-then-fetch, with a constant no-op `fn` so nothing re-renders).
 *
 * Mounted for the Coco shell lifecycle beside AppModel (`V1AppShellContent.createModels`).
 */
export class CocoBroadcastQuerySyncModel extends BaseModel {
  private readonly _resourceUpdated: ReturnType<
    typeof QueryClientEventWatchable.for<number, unknown>
  >;
  private readonly _recentChats: ReturnType<
    typeof QueryClientEventWatchable.for<number, unknown>
  >;
  private readonly _creditBalance: ReturnType<
    typeof QueryClientEventWatchable.for<number, { orgId: string }>
  >;
  private readonly _toolCredential: ReturnType<
    typeof QueryClientEventWatchable.for<number, { orgId: string }>
  >;

  constructor(props: {
    orgId: string;
    queryClient: QueryClient;
    watchablesFactory: WatchablesFactory;
  }) {
    super('coco-broadcast-query-sync');
    const { orgId, queryClient } = props;
    const { watchablesFactory } = props;

    this._resourceUpdated =
      watchablesFactory.makeResourceUpdatedQuerySyncWatchable({
        orgId,
        queryClient,
      });

    this._recentChats =
      watchablesFactory.makeRecentChatsUpdatedQuerySyncWatchable({
        orgId,
        queryClient,
      });

    this._creditBalance =
      watchablesFactory.makeCreditBalanceUpdatedQuerySyncWatchable({
        orgId,
        queryClient,
      });

    this._toolCredential =
      watchablesFactory.makeToolCredentialUpdatedQuerySyncWatchable({
        orgId,
        queryClient,
      });
  }

  dispose(): void {
    this._resourceUpdated.unsubscribe();
    this._recentChats.unsubscribe();
    this._creditBalance.unsubscribe();
    this._toolCredential.unsubscribe();
  }
}
